import { useState, type FormEvent } from 'react';
import { Button, DialogActions, DialogContent, TextField } from '@mui/material';
import type { SettingsOptionRow } from '../../types';
import type { Category, MachineOption } from '../../../planning/types';
import { OPTION_MODAL_LABELS as LABELS } from '../../../../utils/labels';
import AppDialog from '../../../../components/AppDialog';
import { useModalHistory } from '../../../../utils/useModalHistory';
import SpinnerButton from '../../../../components/SpinnerButton';
import SearchableCheckboxList from '../../../../components/SearchableCheckboxList';

interface OptionModalProps {
  option?: SettingsOptionRow;
  // Full category catalog — only passed when this modal is opened from OptionsTab (the
  // settings management flow). Omitted when used as a quick-create sub-modal elsewhere in the
  // app (e.g. MachineFormPage), which keeps this modal in its original name-only form.
  categories?: Category[];
  // Every option already known in the org (active list is enough — this only guards against
  // picking a name that's already visible/selectable, not against archived names too), used to
  // block creating a second option with the same name. Passed from every call site that creates
  // options (OptionsTab, and the quick-create sub-modal usages in CategoryModal/MachineFormPage/
  // DetailActionDialog) so the check applies everywhere, not just one flow.
  existingOptions?: MachineOption[];
  // Shown only when creating an option from within CategoryModal's "+ Nieuwe optie toevoegen"
  // row — clarifies that the new option lands in the org-wide catalog, not just this category.
  showAddedToListHint?: boolean;
  // Pre-checks these categories when creating a new option (ignored in edit mode, where
  // option.category_ids already drives the initial selection) — used by MachineFormPage's
  // quick-create flow to default-select whichever category the machine form already has chosen.
  initialCategoryIds?: string[];
  onClose: () => void;
  onSave: (name: string, categoryIds: string[]) => Promise<boolean>;
}

export default function OptionModal({ option, categories, existingOptions, showAddedToListHint, initialCategoryIds, onClose, onSave }: OptionModalProps) {
  useModalHistory(onClose);
  const [name, setName] = useState(option?.name ?? '');
  const [nameError, setNameError] = useState('');
  const [saving, setSaving] = useState(false);
  const [categoryIds, setCategoryIds] = useState<string[]>(option?.category_ids ?? initialCategoryIds ?? []);
  const [search, setSearch] = useState('');

  const isCreate = !option;
  const showCategoriesPanel = !!categories;
  const allCategories = categories ?? [];

  const toggleCategory = (id: string) => {
    setCategoryIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(LABELS.nameRequired);
      return;
    }
    const isDuplicate = existingOptions?.some(
      (o) => o.id !== option?.id && o.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setNameError(LABELS.nameDuplicate);
      return;
    }
    setSaving(true);
    const ok = await onSave(trimmedName, categoryIds);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth={showCategoriesPanel ? 'sm' : 'xs'}
      fullWidth
      title={isCreate ? LABELS.createTitle : LABELS.editTitle}
      confirmLabel={LABELS.save}
      confirmFormId="option-form"
      confirmDisabled={saving}
      confirmLoading={saving}
      testId="option-modal"
    >
      <form id="option-form" onSubmit={handleSubmit} noValidate>
        <DialogContent className="flex flex-col gap-4">
          <TextField
            label={LABELS.nameLabel}
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
            required
            autoFocus
            fullWidth
            className="mt-2"
            error={!!nameError}
            helperText={nameError || undefined}
          />

          {isCreate && showAddedToListHint && (
            <p className="text-sm text-text-secondary mt-0 mb-0">{LABELS.addedToListHint}</p>
          )}

          {showCategoriesPanel && (
            <SearchableCheckboxList
              heading={LABELS.categoriesHeading}
              info={LABELS.categoriesInfo}
              searchPlaceholder={LABELS.categorySearchPlaceholder}
              search={search}
              onSearchChange={setSearch}
              items={allCategories}
              selectedIds={categoryIds}
              onToggle={toggleCategory}
              noItemsText={LABELS.noCategoriesFound}
              searchSize="small"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">
            {LABELS.cancel}
          </Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
